import { useState, useContext } from 'react';
import { Button } from '@/components/ui/button';
import { X, Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AppContext } from '@/App';

function ImageUpload({ images, onImagesChange, maxImages = 5 }) {
  const { API } = useContext(AppContext);
  const [previews, setPreviews] = useState(images || []);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    
    if (previews.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        if (file.size > 5000000) { // 5MB limit
          toast.error(`File ${file.name} is too large. Maximum 5MB per image.`);
          continue;
        }

        // Upload to backend
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await axios.post(`${API}/upload/image`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          // The backend returns relative URL, convert to full URL
          const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
          uploadedUrls.push(fullUrl);
        } catch (error) {
          console.error('Upload error:', error);
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      if (uploadedUrls.length > 0) {
        const newPreviews = [...previews, ...uploadedUrls];
        setPreviews(newPreviews);
        onImagesChange(newPreviews);
        toast.success(`${uploadedUrls.length} image(s) uploaded successfully`);
      }
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    const newPreviews = previews.filter((_, i) => i !== index);
    setPreviews(newPreviews);
    onImagesChange(newPreviews);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Images (Optional)</label>
        <span className="text-xs text-gray-500">{previews.length}/{maxImages} images</span>
      </div>
      
      {/* Preview Grid */}
      {previews.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {previews.map((preview, index) => (
            <div key={index} className="relative group">
              <img
                src={preview}
                alt={`Preview ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg border"
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-image-${index}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {previews.length < maxImages && (
        <label className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg ${
          uploading ? 'cursor-wait opacity-50' : 'cursor-pointer hover:bg-gray-50'
        } transition-colors`}>
          <div className="flex flex-col items-center">
            {uploading ? (
              <>
                <Loader2 className="w-8 h-8 text-purple-600 mb-1 animate-spin" />
                <span className="text-sm text-gray-500">Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400 mb-1" />
                <span className="text-sm text-gray-500">Click to upload images</span>
                <span className="text-xs text-gray-400">PNG, JPG up to 5MB</span>
              </>
            )}
          </div>
          <input
            type="file"
            className="hidden"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            disabled={uploading}
            data-testid="image-upload-input"
          />
        </label>
      )}
    </div>
  );
}

export default ImageUpload;
